import { useContext, useEffect, useMemo } from 'react';
import { Timeline } from 'antd';
import { useScrollDirection } from 'react-use-scroll-direction';
import { ActivityLogContext } from 'contexts/activityLog';
import { ActivityLogListContainer } from '../styles';
import ActivityLogItem from './ActivityLogItem';
import { ACTION_TYPES_BYPASSED } from 'contexts/activityLog/types';
import { useFlags } from 'launchdarkly-react-client-sdk';

const isInViewport = (el) => {
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
  );
};

const ActivityLogList = (): JSX.Element => {
  const {
    table,
    actions,
    fetching,
    filters: { page },
    isEmpty,
  } = useContext(ActivityLogContext);
  const { dashboardAccountPlaylistAssignment } = useFlags();
  const showAccountPlaylistActions = Boolean(dashboardAccountPlaylistAssignment);
  const { isScrollingDown, scrollTargetRef } = useScrollDirection();

  // When the LD flag is OFF, hide account-playlist activity entries so the feature is invisible.
  // Client-side filtering is a safe guard in case server-side filtering isn't available.
  const visibleItems = useMemo(() => {
    const items = table?.items ?? [];
    return items.filter((item) => {
      const isAccountPlaylistAction = item.action?.startsWith('account.playlist.');

      if (!showAccountPlaylistActions && isAccountPlaylistAction) return false;

      return (
        Boolean(item.generated_by) ||
        (!item.generated_by && ACTION_TYPES_BYPASSED.includes(item.action))
      );
    });
  }, [showAccountPlaylistActions, table?.items]);

  useEffect(() => {
    if (showAccountPlaylistActions) return;
    if (fetching) return;
    if (!actions?.onChangeFilter) return;

    const loadedCount = table?.items?.length ?? 0;
    const totalCount = table?.count ?? 0;
    if (!loadedCount) return;
    if (visibleItems.length > 0) return;
    if (loadedCount >= totalCount) return;

    // If all fetched items are filtered out (flag OFF), request the next page to avoid an empty UI
    // when lazy loading is based on scroll and there is nothing to scroll.
    actions.onChangeFilter({ name: 'page', value: (parseInt(page) + 1).toString() });
  }, [
    actions,
    fetching,
    page,
    showAccountPlaylistActions,
    table?.count,
    table?.items?.length,
    visibleItems.length,
  ]);

  const handleScroll = (e) => {
    const lastChild = e.target.firstChild?.lastChild;
    if (!lastChild) return;
    if (
      isScrollingDown &&
      !fetching &&
      table.items.length !== table.count &&
      isInViewport(lastChild)
    ) {
      actions.onChangeFilter({ name: 'page', value: (parseInt(page) + 1).toString() });
    }
  };
  return (
    <ActivityLogListContainer onScroll={handleScroll} ref={scrollTargetRef}>
      {!isEmpty && visibleItems.length > 0 && (
        <Timeline>
          {visibleItems.map((item, index) => (
            <ActivityLogItem key={index} activity={item} />
          ))}
        </Timeline>
      )}
    </ActivityLogListContainer>
  );
};

export default ActivityLogList;
